import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { openModal } from "risa-oasis-ui_v2";
import CustomTable from "../../../../components/custom-table/custom-table";
import { LoaderMessage } from "../../../../components/loaderMessage/loaderMessage";
import DocViewerModal from "../../../../components/modals/docViewerModal/docViewerModal";
import NoData from "../../../../components/noData/noData";
import { useDataAccumulatorForSingleOrder } from "../../../../context/dataAccumulatorForSingleOrder";
import { ScreenNamesForBigQuery } from "../../../../enums/eventsData";
import { EventsName } from "../../../../enums/eventsName";
import { ModalId } from "../../../../enums/modalId";
import {
  closeModal,
  setOpenedModalId,
} from "../../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../../workerManagers/workerManager";
import {
  AuthLettersTableColumnsKeys,
  generateAuthLettersTableData,
} from "./authLettersTable";

const AuthLetters = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    authorizationData,
    authorizationDataLoading: isLoading,
    generalOrderDetails,
  } = useDataAccumulatorForSingleOrder();
  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  useEffect(() => {
    if (openedModalId === ModalId.DOC_VIEWER_MODAL) {
      openModal(ModalId.DOC_VIEWER_MODAL);
    }
  }, [openedModalId]);

  const tableData = useMemo(() => {
    if (!authorizationData) return [];
    console.log("authorizationData", authorizationData);
    return generateAuthLettersTableData(authorizationData ?? []);
  }, [authorizationData]);

  const hasLoggedTabView = useRef(false);

  useEffect(() => {
    if (!hasLoggedTabView.current) {
      apiWorkerManager.enqueueAndProcess({
        patient_id:
          generalOrderDetails?.order?.demographics?.patient_id ??
          generalOrderDetails?.order?.demographics?.patient_mrn ??
          "",
        order_id: generalOrderDetails?.order?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.AUTH_LETTERS_TAB_VIEW,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_LETTERS_SCREEN,
        },
      });
      hasLoggedTabView.current = true;
    }

    if (!isLoading && authorizationData) {
      apiWorkerManager.enqueueAndProcess({
        patient_id:
          generalOrderDetails?.order?.demographics?.patient_id ??
          generalOrderDetails?.order?.demographics?.patient_mrn ??
          "",
        order_id: generalOrderDetails?.order?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        event_name: EventsName.AUTH_LETTERS_FETCHED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_LETTERS_SCREEN,
          auth_letters_count: tableData.length,
        },
      });
    }
  }, [isLoading, authorizationData]);

  const handleViewClick = (rowData: any) => {
    apiWorkerManager.enqueueAndProcess({
      patient_id:
        generalOrderDetails?.order?.demographics?.patient_id ??
        generalOrderDetails?.order?.demographics?.patient_mrn ??
        "",
      order_id: generalOrderDetails?.order?.order_id ?? "",
      org_id: getOrgIdForFetchExternalWorklist() || "",
      event_name: EventsName.AUTH_LETTER_VIEW_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_LETTERS_SCREEN,
        document_name:
          rowData?.pdfTitle || rowData?.description || "Auth Letter",
      },
    });

    if (rowData?.pdfPath) {
      dispatch(
        setOpenedModalId({
          id: ModalId.DOC_VIEWER_MODAL,
          metaData: {
            filePath: rowData.pdfPath,
            title: rowData.pdfTitle || "Auth Letter",
            hideFooter: true,
          },
        }),
      );
    }
  };

  return (
    <div className="auth-letters--container flex h-full flex-col gap-2">
      {isLoading ? (
        <LoaderMessage message="Fetching Auth Letters..." />
      ) : (
        <>
          {tableData.length > 0 ? (
            <div className="auth-letters--table">
              <CustomTable
                tableHeaders={AuthLettersTableColumnsKeys}
                tableData={tableData}
                itemsPerPage={tableData.length}
                pagesPerView={5}
                count={tableData.length}
                onReviewButtonClick={handleViewClick}
                tableName="authLetters"
                hideSearchBar={true}
                hidePagination={true}
              />
            </div>
          ) : (
            <div className="flex h-full items-center justify-center rounded-lg bg-gray-50">
              <NoData />
            </div>
          )}
        </>
      )}
      {openedModalId === ModalId.DOC_VIEWER_MODAL && (
        <DocViewerModal
          onClose={() => {
            dispatch(closeModal());
          }}
          type="clinical_attachment"
        />
      )}
    </div>
  );
};

export default AuthLetters;
